/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, April 20th 2023, 11:48:22 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { TResourcePolicy, TResource } from "authorization/abac/types";
import { featurePolicy } from "./FeaturePolicy";
import { pagePolicy } from "./PagePolicy";

export type TResourceId =
  | "invite-workers"
  | "datasets"
  | "addPortals"
  | "customizePortals"
  | "manageTeams"
  | "billing-page"
  | "configureIntegrations"
  | "configureAIWorkers"
  | "configureCustomEmailServer"
  | "configurePortalOnboardingForms"
  | "managePaymentAPICredentials"
  | "configureSMSNotifications"
  | "configurePortalOTPSettings"
  | "managePortalContent"
  | "runSQLQueries";

type TAttributesFromPolicy<TPolicy> = TPolicy extends { checkPolicy: (attributes: infer Attr, subject: any) => boolean }
  ? Attr
  : never;

type TPolicyRules<TPolicy> = Record<
  TResourceId,
  {
    policy: TResourcePolicy<TAttributesFromPolicy<TPolicy>>;
    conditions: TAttributesFromPolicy<TPolicy>;
    name: string;
  }
>;

type TPolicyFromType<TType extends TResource["type"]> = TType extends "feature"
  ? typeof featurePolicy
  : TType extends "page"
  ? typeof pagePolicy
  : null;

export type TAttributesFromType<TType extends TResource["type"]> = TAttributesFromPolicy<TPolicyFromType<TType>>;
export type TRulesFromType<TType extends TResource["type"]> = TPolicyRules<TPolicyFromType<TType>>;

const featureRules: Partial<TPolicyRules<TPolicyFromType<"feature">>> = {
  "invite-workers": {
    policy: featurePolicy,
    name: "Invite Workers",
    conditions: { subscriptionCodes: ["onestop-lite", "onestop-pro", "onestop-premium", "onestop-enterprise"] },
  },
  addPortals: {
    policy: featurePolicy,
    name: "Add New Portals",
    conditions: { subscriptionCodes: ["onestop-lite", "onestop-pro", "onestop-premium", "onestop-enterprise"] },
  },
  manageTeams: {
    policy: featurePolicy,
    name: "Manage Teams",
    conditions: { subscriptionCodes: ["onestop-pro", "onestop-premium", "onestop-enterprise"] },
  },
  datasets: {
    policy: featurePolicy,
    name: "Manage Datasets",
    conditions: { subscriptionCodes: ["onestop-premium", "onestop-enterprise"] },
  },
  customizePortals: {
    policy: featurePolicy,
    name: "Customize Portals",
    conditions: { subscriptionCodes: ["onestop-enterprise"] },
  },
  configureIntegrations: {
    policy: featurePolicy,
    name: "Configure Integrations",
    conditions: { subscriptionCodes: ["onestop-enterprise"] },
  },
  configureAIWorkers: {
    policy: featurePolicy,
    name: "Configure AI Workers",
    conditions: { subscriptionCodes: ["onestop-enterprise"] },
  },
  configurePortalOnboardingForms: {
    policy: featurePolicy,
    name: "Configure Portal Onboarding Forms",
    conditions: { subscriptionCodes: ["onestop-premium", "onestop-enterprise"] },
  },
  configureCustomEmailServer: {
    policy: featurePolicy,
    name: "Configure Custom Email Server",
    conditions: { subscriptionCodes: ["onestop-pro", "onestop-premium", "onestop-enterprise"] },
  },
  managePaymentAPICredentials: {
    policy: featurePolicy,
    name: "Manage Payment API Credentials",
    conditions: { subscriptionCodes: ["onestop-premium", "onestop-enterprise"] },
  },
  configureSMSNotifications: {
    policy: featurePolicy,
    name: "Configure SMS Notifications",
    conditions: { subscriptionCodes: ["onestop-enterprise"] },
  },
  configurePortalOTPSettings: {
    policy: featurePolicy,
    name: "Configure Portal OTP Settings",
    conditions: { subscriptionCodes: ["onestop-pro", "onestop-premium", "onestop-enterprise"] },
  },
  managePortalContent: {
    policy: featurePolicy,
    name: "Manage Portal Content",
    conditions: { subscriptionCodes: ["onestop-enterprise"] },
  },
  runSQLQueries: {
    policy: featurePolicy,
    name: "Run Safe SQL Queries",
    conditions: { subscriptionCodes: ["onestop-enterprise"] },
  },
};

const pageRules: Partial<TPolicyRules<TPolicyFromType<"page">>> = {
  "billing-page": { policy: pagePolicy, name: "Invite Workers", conditions: { permission: "access-billing" } },
};

const policyRules: TPolicyRules<TResourcePolicy<TResource["type"]>> = {
  ...featureRules,
  ...pageRules,
} as any;

export const getPolicyRule = <TType extends TResource["type"]>(
  resourceId: TResourceId
): TRulesFromType<TType>[keyof TRulesFromType<TType>] => {
  return policyRules[resourceId] as any;
};
